import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom"; // Add this import
import Footer from "../components/Footer";
import { motion, AnimatePresence } from "framer-motion"; // Add AnimatePresence
import {
    FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt,
    FaGraduationCap, FaUniversity, FaGlobe,
    FaFilePdf, FaBriefcase, FaCalendarAlt, FaPaperPlane,
    FaSpinner, FaCheckCircle, FaTimes // Add new icons
} from "react-icons/fa";

// Recaptcha component import
import ReCAPTCHA from "react-google-recaptcha";

function MASAYouthAmbassadors() {
    const API_URL = `${process.env.REACT_APP_API_BASE_URL}/ui/hiring-banners`;
    const API_MEDIA_UPLOAD = `${process.env.REACT_APP_API_BASE_URL}/UImedia/upload`;
    const API_APPLY = `${process.env.REACT_APP_API_BASE_URL}/UI/YouthAmbassadorApplication`;
    const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [recaptchaValue, setRecaptchaValue] = useState(null);
    const [captchaError, setCaptchaError] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false); // New state for popup
    
    // Create ref for reCAPTCHA
    const recaptchaRef = useRef(null);
    const navigate = useNavigate(); // For navigation

    /* ================= FORM STATE ================= */
    const [formData, setFormData] = useState({
        fullName: "",
        age: "",
        mobile: "",
        email: "",
        city: "",
        state: "",
        educationLevel: "",
        courseName: "",
        collegeName: "",
        languages: "",
        resume: null,
        hasExperience: "No",
        role: "",
        duration: "",
    });

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData({
            ...formData,
            [name]: files ? files[0] : value,
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert("File size should be less than 5MB");
                return;
            }
            
            // Check file type
            const allowedTypes = ['application/pdf', 'application/msword', 
                                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                alert("Only PDF, DOC, and DOCX files are allowed");
                return;
            }
            
            setFormData({
                ...formData,
                resume: file,
            });
        }
    };

    const handleCaptchaChange = (value) => {
        setRecaptchaValue(value);
        setCaptchaError(false);
    };

    const handleCaptchaExpired = () => {
        setRecaptchaValue(null);
        setCaptchaError(true);
    };

    const uploadResume = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "resume");
        formData.append("folder", "youth_ambassador_resumes");

        try {
            const response = await axios.post(API_MEDIA_UPLOAD, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                },
            });
            
            if (response.data && response.data.id) {
                return response.data.id;
            } else {
                console.error("Invalid response or missing id:", response.data);
                throw new Error("Upload failed: Invalid response from server");
            }
        } catch (error) {
            console.error("Resume upload error:", {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            throw new Error(error.response?.data?.message || "Failed to upload resume");
        }
    };

    const submitApplication = async (applicationData) => {
        try {
            const response = await axios.post(API_APPLY, applicationData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.data && response.data.success) {
                return response.data;
            } else {
                throw new Error("Application submission failed");
            }
        } catch (error) {
            console.error("Application submission error:", error);
            throw new Error(error.response?.data?.message || "Failed to submit application");
        }
    };

    // Function to handle redirect after success
    const handleSuccessRedirect = () => {
        // Show popup first
        setShowSuccessPopup(true);
        
        // Reset form
        setFormData({
            fullName: "",
            age: "",
            mobile: "",
            email: "",
            city: "",
            state: "",
            educationLevel: "",
            courseName: "",
            collegeName: "",
            languages: "",
            resume: null,
            hasExperience: "No",
            role: "",
            duration: "",
        });
        
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
        setRecaptchaValue(null);
        setUploadProgress(0);
        
        // Set a timeout to redirect after 3 seconds
        setTimeout(() => {
            navigate("/"); // Redirect to home page
        }, 3000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if recaptcha is verified
        if (!recaptchaValue) {
            setCaptchaError(true);
            alert("Please verify that you are not a robot by completing the reCAPTCHA.");
            return;
        }

        // Validate required fields
        const requiredFields = [
            'fullName', 'age', 'mobile', 'email', 'city', 'state',
            'educationLevel', 'courseName', 'collegeName'
        ];
        
        for (const field of requiredFields) {
            if (!formData[field]) {
                alert(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
                return;
            }
        }

        if (!formData.resume) {
            alert("Please upload your resume");
            return;
        }

        setSubmitting(true);
        setSubmitError("");
        setSubmitSuccess(false);

        try {
            // Step 1: Upload resume
            const resumeMediaId = await uploadResume(formData.resume);

            // Step 2: Prepare application data
            const applicationPayload = {
                fullName: formData.fullName,
                age: parseInt(formData.age, 10),
                mobile: formData.mobile,
                email: formData.email.toLowerCase(),
                city: formData.city,
                state: formData.state,
                educationLevel: formData.educationLevel,
                courseName: formData.courseName,
                collegeName: formData.collegeName,
                languages: formData.languages || "",
                resumeMediaId: resumeMediaId,
                hasExperience: formData.hasExperience,
                role: formData.hasExperience === "Yes" ? (formData.role || "") : "",
                duration: formData.hasExperience === "Yes" ? (formData.duration ? formData.duration.toString() : "") : "",
            };

            // Step 3: Submit application
            const result = await submitApplication(applicationPayload);

            setSubmitSuccess(true);
            
            // Handle success with popup and redirect
            handleSuccessRedirect();

        } catch (error) {
            console.error("Error in submission process:", error);
            setSubmitError(error.message || "An error occurred during submission");
            alert(`Submission failed: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    /* ================= HTML DECODE ================= */
    const decodeHtml = (html) => {
        if (!html) return "";
        const textarea = document.createElement("textarea");
        textarea.innerHTML = html;
        return textarea.value;
    };

    /* ================= FETCH BANNER ================= */
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(API_URL);
                const banners = Array.isArray(res.data) ? res.data : [];
                const activeBanner = banners.find((b) => b.isActive) || banners[0];
                setData(activeBanner || null);
            } catch (error) {
                console.error("Failed to fetch UI hiring banners:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    /* ================= AUTO SLIDER ================= */
    useEffect(() => {
        if (!data?.bannerImages?.length) return;

        const interval = setInterval(() => {
            setCurrentSlide((prev) =>
                prev === data.bannerImages.length - 1 ? 0 : prev + 1
            );
        }, 4000);

        return () => clearInterval(interval);
    }, [data]);

    /* ================= LOADING ================= */
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-amber-400 mx-auto mb-4"></div>
                    <p className="text-gray-300">Loading application form...</p>
                </div>
            </div>
        );
    }

    /* ================= NO DATA ================= */
    if (!data) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl mb-2">No banner found</h2>
                    <p className="text-gray-400">Please check the data source</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Success Popup Modal */}
            <AnimatePresence>
                {showSuccessPopup && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.8, opacity: 0, y: 20 }}
                            transition={{ type: "spring", damping: 20 }}
                            className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 max-w-md w-full border border-gray-700/50 shadow-2xl"
                        >
                            {/* Success Animation */}
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                                className="flex justify-center mb-6"
                            >
                                <div className="relative">
                                    <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-400 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
                                        <FaCheckCircle className="text-white text-5xl" />
                                    </div>
                                    
                                    {/* Animated circle */}
                                    <motion.div
                                        className="absolute inset-0 border-4 border-green-400 rounded-full"
                                        initial={{ scale: 0.8, opacity: 0.7 }}
                                        animate={{ 
                                            scale: [0.8, 1.2, 1.3],
                                            opacity: [0.7, 0.3, 0]
                                        }}
                                        transition={{ 
                                            duration: 1.5,
                                            repeat: Infinity,
                                            repeatType: "loop"
                                        }}
                                    />
                                    
                                    {/* Pulsing effect */}
                                    <motion.div
                                        className="absolute inset-0 border-2 border-green-300 rounded-full"
                                        initial={{ scale: 0.9, opacity: 0.5 }}
                                        animate={{ 
                                            scale: [0.9, 1.4, 1.5],
                                            opacity: [0.5, 0.1, 0]
                                        }}
                                        transition={{ 
                                            duration: 1.5,
                                            repeat: Infinity,
                                            repeatType: "loop",
                                            delay: 0.3
                                        }}
                                    />
                                </div>
                            </motion.div>
                            
                            {/* Success Message */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="text-center"
                            >
                                <h3 className="text-2xl font-bold text-white mb-2">
                                    Application Submitted!
                                </h3>
                                <p className="text-gray-300 mb-6">
                                    Your application has been submitted successfully. We will contact you soon.
                                </p>
                                
                                {/* Countdown Timer */}
                                <div className="mb-6">
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-full bg-gray-700 rounded-full h-2">
                                            <motion.div
                                                className="h-2 bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
                                                initial={{ width: "100%" }}
                                                animate={{ width: "0%" }}
                                                transition={{ duration: 3, ease: "linear" }}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm mt-2">
                                        Redirecting to home in <span className="text-amber-400 font-bold">3</span> seconds
                                    </p>
                                </div>
                                
                                {/* Close Button (optional) */}
                                <button
                                    onClick={() => navigate("/")}
                                    className="px-6 py-2 bg-gradient-to-r from-amber-500 to-yellow-400 text-gray-900 font-bold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/30"
                                >
                                    Go to Home Now
                                </button>
                            </motion.div>
                            
                            {/* Close Icon (optional) */}
                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                            >
                                <FaTimes className="text-xl" />
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Content */}
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                {/* ================= BANNER SLIDER ================= */}
                <div className="relative w-full overflow-hidden">
                    <div className="relative h-[450px] md:h-[500px] lg:h-[600px] w-full">
                        {data.bannerImages?.map((img, index) => {
                            const imageUrl = img?.id
                                ? `${process.env.REACT_APP_API_BASE_URL}/UImedia/${img.id}/download`
                                : null;

                            return (
                                <motion.div
                                    key={img.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: index === currentSlide ? 1 : 0 }}
                                    transition={{ duration: 0.8 }}
                                    className={`absolute inset-0 ${index === currentSlide ? "z-10" : "z-0"
                                        }`}
                                >
                                    <img
                                        src={imageUrl}
                                        alt={`Banner ${index + 1}`}
                                        className="w-full h-full object-continer"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                                </motion.div>
                            );
                        })}

                        {/* Dots */}
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-30">
                            {data.bannerImages.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`w-3 h-3 rounded-full border-2 border-amber-400 transition-all ${currentSlide === index
                                            ? "bg-amber-400 scale-125"
                                            : "bg-transparent"
                                        }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* ================= CONTENT ================= */}
                <div className="max-w-6xl mx-auto px-4 py-12">
                    {/* Title and SubTitle */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="mb-10 md:mb-16"
                    >
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight text-white">
                            {data.title}
                        </h1>

                        {data.subTitle && (
                            <h2 className="text-xl md:text-2xl text-amber-400 font-medium">
                                {data.subTitle}
                            </h2>
                        )}
                    </motion.div>

                    {/* Description */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 mb-12 border border-gray-700/50 shadow-xl"
                    >
                        <div
                            className="prose prose-invert max-w-none text-gray-300 leading-relaxed text-lg"
                            dangerouslySetInnerHTML={{
                                __html: decodeHtml(data.description),
                            }}
                        />
                    </motion.div>

                    {/* ================= APPLICATION FORM ================= */}
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 md:p-10 shadow-2xl"
                    >
                        <div className="text-center mb-10">
                            <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
                                Application Form
                            </h2>
                            <p className="text-gray-400">Fill in your details to join </p>
                        </div>

                        {submitError && (
                            <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg">
                                <p className="text-red-300 text-center">
                                    ❌ Error: {submitError}
                                </p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Personal Information Section */}
                            <div className="bg-gray-900/40 rounded-xl p-6 border border-gray-700/30">
                                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2 text-amber-300">
                                    <FaUser className="text-amber-400" />
                                    Personal Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="relative">
                                        <FaUser className="absolute left-4 top-4 text-gray-400" />
                                        <input
                                            name="fullName"
                                            placeholder="Full Name *"
                                            required
                                            value={formData.fullName}
                                            className="w-full px-12 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            name="age"
                                            placeholder="Age *"
                                            required
                                            min="16"
                                            max="35"
                                            value={formData.age}
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="relative">
                                        <FaPhone className="absolute left-4 top-4 text-gray-400" />
                                        <input
                                            type="tel"
                                            name="mobile"
                                            placeholder="Mobile Number *"
                                            required
                                            pattern="[0-9]{10}"
                                            title="Please enter a valid 10-digit mobile number"
                                            value={formData.mobile}
                                            className="w-full px-12 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="relative">
                                        <FaEnvelope className="absolute left-4 top-4 text-gray-400" />
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="Email ID *"
                                            required
                                            value={formData.email}
                                            className="w-full px-12 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="relative">
                                        <FaMapMarkerAlt className="absolute left-4 top-4 text-gray-400" />
                                        <input
                                            name="city"
                                            placeholder="City *"
                                            required
                                            value={formData.city}
                                            className="w-full px-12 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="relative">
                                        <FaMapMarkerAlt className="absolute left-4 top-4 text-gray-400" />
                                        <input
                                            name="state"
                                            placeholder="State *"
                                            required
                                            value={formData.state}
                                            className="w-full px-12 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Education Information Section */}
                            <div className="bg-gray-900/40 rounded-xl p-6 border border-gray-700/30">
                                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2 text-amber-300">
                                    <FaGraduationCap className="text-amber-400" />
                                    Educational Background
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="relative">
                                        <select
                                            name="educationLevel"
                                            required
                                            value={formData.educationLevel}
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 appearance-none"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        >
                                            <option value="">Current Education Level *</option>
                                            <option value="School">School (Class 11–12)</option>
                                            <option value="Undergraduate">Undergraduate (UG)</option>
                                            <option value="Postgraduate">Postgraduate (PG)</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <input
                                            name="courseName"
                                            placeholder="Course Name *"
                                            required
                                            value={formData.courseName}
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div className="md:col-span-2 relative">
                                        <FaUniversity className="absolute left-4 top-4 text-gray-400" />
                                        <input
                                            name="collegeName"
                                            placeholder="College / University Name *"
                                            required
                                            value={formData.collegeName}
                                            className="w-full px-12 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Languages & Resume Section */}
                            <div className="bg-gray-900/40 rounded-xl p-6 border border-gray-700/30">
                                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2 text-amber-300">
                                    <FaGlobe className="text-amber-400" />
                                    Languages & Documents
                                </h3>
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Languages Known
                                        </label>
                                        <textarea
                                            name="languages"
                                            placeholder="e.g., English, Hindi, Spanish..."
                                            rows="3"
                                            value={formData.languages}
                                            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                            onChange={handleChange}
                                            disabled={submitting}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Upload Resume / CV *
                                        </label>
                                        <div className="flex items-center gap-4">
                                            <label className="cursor-pointer flex-1">
                                                <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${submitting ? 'border-gray-600 bg-gray-800/20' : 'border-gray-600 hover:border-amber-400 bg-gray-800/30'}`}>
                                                    <FaFilePdf className="text-3xl text-amber-400 mx-auto mb-3" />
                                                    <p className="text-gray-300">
                                                        {submitting ? 'Uploading...' : 'Click to upload resume'}
                                                    </p>
                                                    <p className="text-sm text-gray-500">PDF, DOC, DOCX (Max 5MB)</p>
                                                    {formData.resume && (
                                                        <p className="text-amber-300 mt-2">
                                                            Selected: {formData.resume.name}
                                                        </p>
                                                    )}
                                                    {uploadProgress > 0 && uploadProgress < 100 && (
                                                        <div className="mt-4">
                                                            <div className="w-full bg-gray-700 rounded-full h-2">
                                                                <div 
                                                                    className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                                                                    style={{ width: `${uploadProgress}%` }}
                                                                ></div>
                                                            </div>
                                                            <p className="text-amber-300 text-sm mt-1">
                                                                Uploading: {uploadProgress}%
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type="file"
                                                    name="resume"
                                                    accept=".pdf,.doc,.docx"
                                                    className="hidden"
                                                    onChange={handleFileChange}
                                                    disabled={submitting}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Experience Section */}
                            <div className="bg-gray-900/40 rounded-xl p-6 border border-gray-700/30">
                                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2 text-amber-300">
                                    <FaBriefcase className="text-amber-400" />
                                    Work Experience
                                </h3>
                                <div className="space-y-6">
                                    <div>
                                        <p className="text-gray-300 mb-4">Any prior internship or work experience?</p>
                                        <div className="flex gap-6">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="hasExperience"
                                                    value="Yes"
                                                    checked={formData.hasExperience === "Yes"}
                                                    onChange={handleChange}
                                                    className="w-5 h-5 text-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                                    disabled={submitting}
                                                />
                                                <span className="text-gray-300">Yes</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="hasExperience"
                                                    value="No"
                                                    checked={formData.hasExperience === "No"}
                                                    onChange={handleChange}
                                                    className="w-5 h-5 text-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                                    disabled={submitting}
                                                />
                                                <span className="text-gray-300">No</span>
                                            </label>
                                        </div>
                                    </div>

                                    {formData.hasExperience === "Yes" && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 p-4 bg-gray-800/30 rounded-xl">
                                            <div className="relative">
                                                <input
                                                    name="role"
                                                    placeholder="Role / Position"
                                                    value={formData.role}
                                                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                                    onChange={handleChange}
                                                    disabled={submitting}
                                                />
                                            </div>
                                            <div className="relative">
                                                <FaCalendarAlt className="absolute left-4 top-4 text-gray-400" />
                                                <input
                                                    
                                                    name="duration"
                                                    placeholder="Duration (months)"
                                                    value={formData.duration}
                                                    
                                                    className="w-full px-12 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                                                    onChange={handleChange}
                                                    disabled={submitting}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ================= RECAPTCHA SECTION ================= */}
                            <div className="bg-gray-900/40 rounded-xl p-6 border border-gray-700/30">
                                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2 text-amber-300">
                                    Security Verification
                                </h3>
                                <div className="flex justify-center">
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={RECAPTCHA_SITE_KEY}
                                        onChange={handleCaptchaChange}
                                        onExpired={handleCaptchaExpired}
                                        theme="dark"
                                        className={`${captchaError ? 'border-2 border-red-500 rounded' : ''}`}
                                    />
                                </div>
                                {captchaError && (
                                    <p className="text-red-400 text-sm text-center mt-2">
                                        Please complete the reCAPTCHA verification
                                    </p>
                                )}
                                <p className="text-gray-400 text-sm text-center mt-4">
                                    Please verify that you are not a robot
                                </p>
                            </div>

                            {/* Submit Button */}
                            <div className="text-center pt-8">
                                <button
                                    type="submit"
                                    disabled={submitting || !recaptchaValue}
                                    className="px-12 py-4 bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 text-gray-900 font-bold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-amber-500/30 flex items-center gap-3 mx-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    {submitting ? (
                                        <>
                                            <FaSpinner className="animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane />
                                            Submit Application
                                        </>
                                    )}
                                </button>
                                <p className="text-gray-400 text-sm mt-4">
                                    By submitting, you agree to our terms and conditions
                                </p>
                            </div>
                        </form>
                    </motion.div>
                </div>

                <Footer />
            </div>
        </>
    );
}

export default MASAYouthAmbassadors;